/**
 * Superadmin plans console (ADR-0041 §13). List / create / edit / (de)activate the billing
 * PLANS — catalog-validated templates a tenant's subscription snapshots from. The
 * create/edit dialog renders a FORM generated from the entitlements catalog (axes in
 * code), NOT raw JSON: feature checkboxes + limit number inputs (empty = unlimited).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Sparkles, TriangleAlert } from "lucide-react";
import * as React from "react";

import { EntitlementsEditor } from "@/components/platform/EntitlementsEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type EntitlementsCatalog,
  type Plan,
  type PlanEntitlements,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

const PLANS_KEY = ["platform", "plans"];
const CATALOG_KEY = ["platform", "entitlements-catalog"];

export function PlatformPlansPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Plan | null>(null);

  const plansQ = useQuery({ queryKey: PLANS_KEY, queryFn: () => platformApi.listPlans() });
  const catalogQ = useQuery({
    queryKey: CATALOG_KEY,
    queryFn: () => platformApi.entitlementsCatalog(),
    staleTime: Infinity,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: PLANS_KEY });

  const seed = useMutation({
    mutationFn: () => platformApi.seedDefaultPlans(),
    onSuccess: invalidate,
  });

  const rows = plansQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Piani"
        description="I piani commerciali. Un abbonamento ne cattura uno snapshot: modificarli non tocca i clienti esistenti."
        actions={
          <div className="flex items-center gap-2">
            {rows.length === 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => seed.mutate()}
                disabled={seed.isPending}
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                Crea piani predefiniti
              </Button>
            )}
            <Button size="sm" onClick={() => setCreating(true)} disabled={!catalogQ.data}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Nuovo piano
            </Button>
          </div>
        }
      />
      <PageContent className="space-y-4">
        {plansQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare i piani"
            description={
              plansQ.error instanceof PlatformApiError ? plansQ.error.message : "Errore di rete."
            }
            action={
              <Button variant="ghost" onClick={() => plansQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title="Nessun piano"
            description="Crea i piani predefiniti (Starter / Pro / Business) o aggiungine uno."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Piano</Th>
                  <Th>Chiave</Th>
                  <Th>Stato</Th>
                  <Th>Vendibile</Th>
                  <Th>Pubblico</Th>
                  <Th className="w-20 text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((plan) => (
                  <Tr key={plan.id}>
                    <Td className="font-medium">{plan.name}</Td>
                    <Td className="font-mono text-xs text-muted-foreground">{plan.key}</Td>
                    <Td>
                      <Badge tone={plan.active ? "active" : "neutral"}>
                        {plan.active ? "Attivo" : "Disattivo"}
                      </Badge>
                    </Td>
                    <Td>
                      {/* ADR-0079: a plan with no Stripe Price cannot be bought by ANYONE —
                          the signup shows its card and then refuses. Saying it here is what
                          stops "le registrazioni sono rotte" after a fresh install. */}
                      {plan.stripe_price_id || plan.stripe_price_year_id ? (
                        <Badge tone="active">Sì</Badge>
                      ) : (
                        <Badge tone="warning" title="Nessun prezzo Stripe: sincronizza il piano">
                          Da sincronizzare
                        </Badge>
                      )}
                    </Td>
                    <Td className="text-xs text-muted-foreground">{plan.public ? "Sì" : "No"}</Td>
                    <Td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Modifica"
                        onClick={() => setEditing(plan)}
                      >
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      {creating && catalogQ.data && (
        <PlanDialog
          catalog={catalogQ.data}
          onClose={() => setCreating(false)}
          onSaved={invalidate}
        />
      )}
      {editing && catalogQ.data && (
        <PlanDialog
          catalog={catalogQ.data}
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
    </PageContainer>
  );
}

function emptyEntitlements(catalog: EntitlementsCatalog): PlanEntitlements {
  return {
    features: Object.fromEntries(catalog.features.map((a) => [a.key, Boolean(a.default)])),
    limits: Object.fromEntries(
      catalog.limits.map((a) => [a.key, typeof a.default === "number" ? a.default : null]),
    ),
  };
}

function PlanDialog({
  catalog,
  plan,
  onClose,
  onSaved,
}: {
  catalog: EntitlementsCatalog;
  plan?: Plan;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editingExisting = !!plan;
  const [key, setKey] = React.useState(plan?.key ?? "");
  const [name, setName] = React.useState(plan?.name ?? "");
  const [active, setActive] = React.useState(plan?.active ?? true);
  const [isPublic, setIsPublic] = React.useState(plan?.public ?? true);
  const [price, setPrice] = React.useState(
    plan?.price_cents != null ? String(plan.price_cents / 100) : "",
  );
  const [priceYear, setPriceYear] = React.useState(
    plan?.price_year_cents != null ? String(plan.price_year_cents / 100) : "",
  );
  const [ent, setEnt] = React.useState<PlanEntitlements>(
    plan?.entitlements ?? emptyEntitlements(catalog),
  );
  // The global concurrency defaults — so a `fallback:"global"` axis reads "= globale: N"
  // when empty, not "illimitato" (ADR-0040).
  const settingsQ = useQuery({
    queryKey: ["platform", "settings"],
    queryFn: () => platformApi.getSettings(),
  });
  const globalCap = (k: string): number | undefined => {
    if (k === "max_concurrent_workflow_runs") return settingsQ.data?.max_concurrent_workflow_runs;
    if (k === "max_concurrent_etl_runs") return settingsQ.data?.max_concurrent_etl_runs;
    return undefined;
  };

  const priceCents = price.trim() === "" ? null : Math.max(0, Math.round(Number(price) * 100));
  const priceYearCents =
    priceYear.trim() === "" ? null : Math.max(0, Math.round(Number(priceYear) * 100));

  const save = useMutation({
    mutationFn: () =>
      editingExisting
        ? platformApi.updatePlan(plan!.id, {
            name: name.trim(),
            active,
            public: isPublic,
            entitlements: ent,
            price_cents: priceCents,
            price_year_cents: priceYearCents,
          })
        : platformApi.createPlan({
            key: key.trim(),
            name: name.trim(),
            active,
            public: isPublic,
            entitlements: ent,
            price_cents: priceCents,
            price_year_cents: priceYearCents,
          }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  // Tray→Stripe catalog sync (ADR-0041 Fase 1) — provisions the Product+Prices from the NET
  // price and AUTO-fills the ids. The Price IDs are Stripe-managed (read-only below).
  const [syncMsg, setSyncMsg] = React.useState<string | null>(null);
  const sync = useMutation({
    mutationFn: () => platformApi.syncPlanStripe(plan!.id),
    onSuccess: (r) => {
      setSyncMsg(r.actions.length ? r.actions.join(" · ") : "Già sincronizzato.");
      onSaved(); // refetch the list so the managed ids refresh
    },
    onError: (e) =>
      setSyncMsg(
        e instanceof PlatformApiError && e.message.includes("stripe_disabled")
          ? "Stripe non è configurato (manca la chiave)."
          : "Sincronizzazione non riuscita.",
      ),
  });

  const error = save.isError
    ? save.error instanceof PlatformApiError && save.error.status === 409
      ? "Esiste già un piano con questa chiave."
      : save.error instanceof PlatformApiError
        ? save.error.message
        : "Salvataggio non riuscito."
    : null;
  const valid = name.trim().length > 0 && (editingExisting || /^[a-z][a-z0-9_]*$/.test(key.trim()));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingExisting ? `Modifica — ${plan!.name}` : "Nuovo piano"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="plan-key">Chiave</Label>
              <Input
                id="plan-key"
                value={key}
                disabled={editingExisting}
                onChange={(e) => setKey(e.target.value.toLowerCase())}
                placeholder="starter"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-name">Nome</Label>
              <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="plan-price">Prezzo mensile netto (€)</Label>
              <Input
                id="plan-price"
                type="number"
                min={0}
                step="1"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-price-year">Prezzo annuale (€)</Label>
              <Input
                id="plan-price-year"
                type="number"
                min={0}
                step="1"
                value={priceYear}
                onChange={(e) => setPriceYear(e.target.value)}
                placeholder="(nessuna offerta annuale)"
              />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            Prezzi <strong>netti</strong> (IVA esclusa): l'IVA è calcolata al checkout da Stripe
            Tax. Il prezzo annuale è il TOTALE per un anno; lascialo vuoto per non offrire la
            cadenza annuale.
          </p>

          {/* Stripe catalog (ADR-0041 Fase 1) — Tray è la fonte di verità: i Price ID sono
              GESTITI da Stripe (provisionati dalla sync), non si incollano a mano. */}
          <fieldset className="space-y-2 rounded-md border border-border/60 p-3">
            <legend className="px-1 text-xs font-medium text-muted-foreground">Stripe</legend>
            {editingExisting ? (
              <>
                <dl className="space-y-1 text-xs">
                  {(
                    [
                      ["Prodotto", plan!.stripe_product_id],
                      ["Price mensile", plan!.stripe_price_id],
                      ["Price annuale", plan!.stripe_price_year_id],
                    ] as const
                  ).map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="font-mono text-foreground">
                        {val ?? <span className="text-muted-foreground">— non sincronizzato</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={sync.isPending}
                  onClick={() => sync.mutate()}
                >
                  {sync.isPending ? "Sincronizzazione…" : "Sincronizza con Stripe"}
                </Button>
                {syncMsg && <p className="text-xs text-muted-foreground">{syncMsg}</p>}
                <p className="text-xs text-muted-foreground">
                  Provisiona Product + Price (netti) dal prezzo qui sopra; gli ID sono per-ambiente
                  (test in dev, live in prod). Salva prima eventuali modifiche al prezzo.
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Salva il piano, poi potrai sincronizzarlo con Stripe per generare i Price.
              </p>
            )}
          </fieldset>

          {/* ONE editor, shared with the per-licence customisation (ADR-0073 overhaul):
              the axes live in backend code, so a second copy of this form would drift the
              moment an axis grew a rule (the `fallback:"global"` label already is one). */}
          <EntitlementsEditor catalog={catalog} value={ent} onChange={setEnt} globalCap={globalCap} />

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Attivo
          </label>
          {/* A NON-public plan never appears in the tenant catalog / signup / self-serve
              switch — only the superadmin can assign it (VIP custom plans, retired plans).
              The backend filter is `public AND active`; this just exposes the flag. */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            Visibile ai tenant (acquistabile in self-service)
          </label>

          {error && <p className="text-sm text-danger">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
